/**
 * rosterHelpers.ts
 * SINGLE SOURCE OF TRUTH for roster completion logic
 * 
 * CRITICAL: This file mirrors pools/helpers.py on the backend.
 * NO completion logic should exist outside this file.
 * 
 * All components and hooks MUST import from here.
 * 
 * SSOT Verification Command:
 * grep -r "slot.units && slot.units > 0" ui/src/components/ ui/src/hooks/
 * Expected: 0 matches (all logic delegated here)
 */

export interface RosterSlot {
  player_prop_id: number | null;
  side: 'OVER' | 'UNDER' | null;
  units: number;
}

/**
 * Check if a single slot is complete.
 * 
 * A slot is COMPLETE when it has:
 * - A selected prop (player_prop_id is not null)
 * - A side (OVER or UNDER)
 * - Units assigned (units > 0)
 * 
 * Mirrors: pools/helpers.py -> is_slot_complete()
 */
export const isSlotComplete = (slot: RosterSlot | null | undefined): boolean => {
  if (!slot) return false;
  
  return Boolean(
    slot.player_prop_id !== null &&
    slot.side !== null &&
    slot.units > 0
  );
};

/**
 * Count completed slots in a roster.
 * 
 * Mirrors: pools/helpers.py -> count_completed_slots()
 */
export const countCompletedSlots = (roster: RosterSlot[]): number => {
  if (!roster || !Array.isArray(roster)) return 0;
  return roster.filter(isSlotComplete).length;
};

/**
 * Calculate total units allocated across all slots.
 * 
 * Mirrors: pools/helpers.py -> calculate_total_units()
 */
export const calculateTotalUnits = (roster: RosterSlot[]): number => {
  if (!roster || !Array.isArray(roster)) return 0;
  return roster.reduce((sum, slot) => sum + (slot?.units || 0), 0);
};

/**
 * Check if roster is fully complete (ready for "lock").
 * 
 * Mirrors: pools/helpers.py -> is_roster_complete()
 * 
 * @param roster - Array of roster slots
 * @param requiredSlots - Number of slots required (default 7)
 * @param requiredUnits - Total units required (default 100)
 */
export const isRosterComplete = (
  roster: RosterSlot[], 
  requiredSlots: number = 7,
  requiredUnits: number = 100
): boolean => {
  if (!roster || !Array.isArray(roster)) return false;
  
  return (
    countCompletedSlots(roster) >= requiredSlots &&
    calculateTotalUnits(roster) === requiredUnits
  );
};

/**
 * Get remaining cap space.
 * 
 * @param roster - Array of roster slots
 * @param totalCap - Total cap available (default 100)
 */
export const getRemainingCap = (
  roster: RosterSlot[],
  totalCap: number = 100
): number => {
  return totalCap - calculateTotalUnits(roster);
};

/**
 * Find first incomplete slot index.
 * Returns -1 if all slots are complete.
 */
export const findFirstIncompleteSlot = (roster: RosterSlot[]): number => {
  if (!roster || !Array.isArray(roster)) return 0;
  const index = roster.findIndex(slot => !isSlotComplete(slot));
  return index >= 0 ? index : -1;
};
